import { noop } from "@pjs/utilities";
import { RefObject } from "react";
import { IEditor } from "../../editor/interfaces/IEditor";
import { IEditorFactoryConfig } from "../../editor-factories/interfaces/IEditorFactoryConfig";
import { Behaviour } from "../types/Behaviour";

/**
 *
 * @param element
 * @param boundary
 * @param proposedElementX This X coordinate is RELATIVE to the boundary element, not absolute to the viewport
 * @param proposedElementY This Y coordinate is RELATIVE to the boundary element, not absolute to the viewport
 * @param positionElement Callback which is provided the new X and Y values for the element
 */
function moveElementWithinBoundary(element: HTMLElement, boundary: HTMLElement, proposedElementX: number, proposedElementY: number): [number, number] {
    const boundaryRect = boundary.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    let finalX: number = proposedElementX;
    let finalY: number = proposedElementY;

    const elementRightEdge = proposedElementX + elementRect.width;

    if (elementRightEdge > boundary.clientWidth) {
        finalX = boundary.clientWidth - elementRect.width;

        // Should be prevented via css but in the event the dialog exceeds its container, we default x to 0
        if (finalX < 0) {
            finalX = 0;
        }
    }

    if (proposedElementX < 0) {
        finalX = 0;
    }

    if (proposedElementY < boundary.scrollTop) {
        finalY = boundary.scrollTop;
    }

    const boundaryVisibleBottom = boundary.scrollTop + boundaryRect.height;
    const elementBottomEdge = proposedElementY + elementRect.height;

    if (elementBottomEdge > boundaryVisibleBottom) {
        // Edge case for mobile landscape, allows element to be moved below the visible area
        if (boundaryRect.height < elementRect.height) {
            if (elementBottomEdge > boundary.scrollHeight) {
                finalY = boundary.scrollHeight - elementRect.height;
            }
        } else {
            finalY = boundaryVisibleBottom - elementRect.height;
        }
    }

    return [finalX, finalY];
}

function calculateOriginalDialogPosition(dialog: HTMLElement, boundary: HTMLElement, editorToolbar: HTMLElement): [number, number] {
    const boundaryRect = boundary.getBoundingClientRect();
    const dialogRect = dialog.getBoundingClientRect();
    const editorToolbarRect = editorToolbar.getBoundingClientRect();
    const offset = 15;
    const left = editorToolbarRect.right - dialogRect.width - offset - boundaryRect.left;
    const top = Math.abs(boundaryRect.top - boundary.scrollTop - editorToolbarRect.bottom - offset);

    return [left, top];
}

function repositionDialog(boundaryRef: RefObject<HTMLElement>, editor: IEditor, _: IEditorFactoryConfig): () => void {
    editor.enabled.subscribe((editorState) => {
        let currentTop = 0;
        let currentLeft = 0;
        let resizeObserver: ResizeObserver | null = null;

        if (!editorState.isEnabled || !editorState.editor.plugins.has("Dialog")) {
            return;
        }

        const dialogPlugin = editorState.editor.plugins.get("Dialog");

        editorState.editor.ui.focusTracker.on("change:isFocused", () => {
            if (dialogPlugin.isOpen && !editorState.editor.ui.focusTracker.isFocused) {
                // eslint-disable-next-line @typescript-eslint/unbound-method
                const temp = editorState.editor.editing.view.focus;
                editorState.editor.editing.view.focus = noop;
                dialogPlugin.hide();
                editorState.editor.editing.view.focus = temp;
            }
        });

        dialogPlugin.on("show", () => {
            const dialogView = dialogPlugin.view;
            if (dialogView === undefined) {
                return;
            }

            const overlay = dialogView.element as HTMLElement;
            const dialogElement = overlay.firstElementChild as HTMLElement;

            boundaryRef.current?.append(overlay);
            overlay.parentElement?.setAttribute("dir", "ltr");

            // eslint-disable-next-line @typescript-eslint/unbound-method
            const originalMoveTo = dialogView.moveTo;

            dialogView.moveTo = (x, y) => {
                const [left, top] = moveElementWithinBoundary(dialogElement, boundaryRef.current as HTMLElement, x, y);
                originalMoveTo.call(dialogView, left, top);
                currentTop = top;
                currentLeft = left;
            };

            dialogView.updatePosition = () => {
                const [x, y] = calculateOriginalDialogPosition(
                    dialogElement,
                    boundaryRef.current as HTMLElement,
                    editorState.editor.ui.view.toolbar.element as HTMLElement
                );
                dialogView.moveTo(x, y);
            };

            // Re-run the logic to position the dialog within the boundary when the boundary is resized
            resizeObserver = new ResizeObserver(() => {
                dialogView.moveTo(currentLeft, currentTop);
            });

            resizeObserver.observe(boundaryRef.current as HTMLElement);
        });

        dialogPlugin.on("hide", () => {
            if (resizeObserver !== null) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
        });
    });

    return noop;
}

export const repositionDialogBehaviourFactory = (boundaryRef: RefObject<HTMLElement>): Behaviour => {
    return repositionDialog.bind(null, boundaryRef);
};
